import { useEffect, useState } from "react";
import { Copy, Download, Loader2, Plus } from "lucide-react";
import { http, errorText } from "@/lib/adminApi";
import { shareImageUrl } from "@/lib/posts";
import { TipDrafts } from "@/pages/admin/TipDrafts";

const field = "w-full rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-white placeholder:text-slate-500 focus:border-[var(--teal)] focus:outline-none";
const EMPTY = { title: "", description: "", before: "", after: "", body: "" };
const SITE = "https://verbulatresumebuilder.com";

const linkedinText = (p) => `${p.title}\n\nBefore: ${p.before}\nAfter: ${p.after}\n\n${p.description}\n\nFull tip: ${SITE}/blog/${p.slug}\n\n#resume #jobsearch #careers`;

export const TipsPanel = () => {
  const [posts, setPosts] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState("");
  useEffect(() => {
    http.get("/admin/posts").then((r) => setPosts(Array.isArray(r.data?.posts) ? r.data.posts : [])).catch(() => setPosts([]));
  }, []);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(""), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const { data } = await http.post("/admin/posts", f);
      setPosts((l) => [data, ...(l || [])]);
      setF(EMPTY);
    } catch (err) { setMsg(errorText(err)); }
    setBusy(false);
  };
  const toggle = async (p) => {
    const published = !p.published;
    setPosts((l) => l.map((x) => (x.slug === p.slug ? { ...x, published } : x)));
    try { await http.patch(`/admin/posts/${p.slug}`, { published }); }
    catch { setPosts((l) => l.map((x) => (x.slug === p.slug ? { ...x, published: p.published } : x))); }
  };
  const copy = async (p) => { try { await navigator.clipboard.writeText(linkedinText(p)); setCopied(p.slug); } catch { setCopied(""); } };

  return (
    <div className="mt-10 space-y-6" data-testid="admin-tips-panel">
      <p className="text-sm text-slate-300">Publish one tip a week. Each gets its own page on your blog plus a ready-made before/after image and post text for LinkedIn.</p>
      <TipDrafts onPublished={(post) => setPosts((l) => [post, ...(l || [])])} />
      <form onSubmit={publish} className="space-y-3 rounded-2xl border border-white/10 bg-[var(--navy)] p-5 sm:p-6" data-testid="tip-form">
        <p className="font-semibold text-white">New weekly tip</p>
        <input required className={field} placeholder="Title, e.g. Use the result your manager noticed" value={f.title} onChange={set("title")} data-testid="tip-title-input" />
        <input required className={field} placeholder="One-sentence summary (shown on Google and the blog list)" value={f.description} onChange={set("description")} data-testid="tip-description-input" />
        <div className="grid gap-3 sm:grid-cols-2">
          <textarea required rows={2} className={field} placeholder="Before bullet" value={f.before} onChange={set("before")} data-testid="tip-before-input" />
          <textarea required rows={2} className={field} placeholder="After bullet" value={f.after} onChange={set("after")} data-testid="tip-after-input" />
        </div>
        <textarea required rows={6} className={field} value={f.body} onChange={set("body")} data-testid="tip-body-input"
          placeholder={"The tip itself. Leave a blank line between paragraphs.\n\n## Start a line with ## for a subheading"} />
        {msg && <p className="text-sm text-[var(--amber)]" role="alert">{msg}</p>}
        <button disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] disabled:opacity-60" data-testid="tip-publish-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publish tip
        </button>
      </form>
      {!posts && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--teal)]" />}
      <ul className="space-y-3">
        {(posts || []).map((p) => (
          <li key={p.slug} className={`rounded-2xl border p-5 ${p.published ? "border-white/10 bg-[var(--navy)]" : "border-white/5 opacity-60"}`} data-testid={`tip-${p.slug}`}>
            <p className="font-semibold text-white">{p.title}</p>
            <p className="mt-1 text-xs text-slate-400">{p.date} · {p.published ? "Live" : "Hidden"} · /blog/{p.slug}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={shareImageUrl(p) || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[var(--teal)]/50 px-4 py-2 text-sm text-[var(--teal)]" data-testid={`tip-image-${p.slug}`}>
                <Download className="h-4 w-4" /> LinkedIn image
              </a>
              <button onClick={() => copy(p)} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-slate-200" data-testid={`tip-copy-${p.slug}`}>
                <Copy className="h-4 w-4" /> {copied === p.slug ? "Copied!" : "Copy LinkedIn text"}
              </button>
              <button onClick={() => toggle(p)} className="rounded-full border border-white/20 px-4 py-2 text-sm text-slate-300" data-testid={`tip-toggle-${p.slug}`}>
                {p.published ? "Hide" : "Publish again"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SubscribersPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    http.get("/admin/subscribers").then((r) => setData(r.data || { subscribers: [] })).catch(() => setData({ subscribers: [] }));
  }, []);
  if (!data) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-[var(--teal)]" />;
  const list = Array.isArray(data.subscribers) ? data.subscribers : [];
  const csv = () => {
    const rows = [["email", "opted_in_to_offers", "last_score", "first_seen"], ...list.map((s) => [s.email, s.marketing_opt_in ? "yes" : "no", s.last_score ?? "", s.created_at])];
    const url = URL.createObjectURL(new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "verbulat-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="mt-10" data-testid="admin-subscribers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-slate-200"><span className="font-serif-d text-4xl text-white">{data.opted_in ?? 0}</span> opted in to offers · {list.length} total checker emails</p>
        <button onClick={csv} disabled={!list.length} className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] disabled:opacity-40" data-testid="subscribers-export-button">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-400">Only send offers to people marked “yes”. Everyone else only asked for their score. Use the Send offer tab to email the “yes” list.</p>
      <ul className="mt-6 divide-y divide-white/10 rounded-2xl border border-white/10">
        {list.slice(0, 100).map((s) => (
          <li key={s.email} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-sm">
            <span className="text-white">{s.email}</span>
            <span className="text-slate-400">{s.marketing_opt_in ? "Offers: yes" : "Score only"} · score {s.last_score ?? "-"}</span>
          </li>
        ))}
        {list.length === 0 && <li className="px-5 py-4 text-sm text-slate-300">No emails yet.</li>}
      </ul>
    </div>
  );
};
